/**
 * 용량 룰 검수 목록 페이지 (계획 C-5·C-7).
 *
 * - 기본 필터: 대표 룰 + `pending_review`(성공기준).
 * - 서버 offset 페이징(50건). 전량 조회 후 클라이언트 슬라이스 금지.
 * - 필터/offset은 URL 쿼리스트링으로 동기화(계획 D16) — 상세 왕복·새로고침·뒤로가기에 견고.
 * - 비즈니스 로직(훅·네비게이션)은 여기서, 테이블 렌더는 DoseRuleListSection에 위임.
 */
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import DoseRuleListSection from "components/organisms/DoseRuleListSection";
import { openDoseReviewTestSlide } from "./openDoseReviewTestSlide";
import { useDoseRules, useDoseRuleStats } from "hooks/doseRules/useDoseRules";
import { FILTERABLE_STATUSES } from "utils/doseRule";
import {
  AdminDoseRuleListItem,
  DoseRuleStatus,
  FindDoseRulesQuery,
} from "services/api/doseRules/types";

const LIMIT = 50;

const PageContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const StatCard = styled.div`
  min-width: 120px;
  padding: 12px 16px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 12px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  font-size: 14px;
`;

const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  cursor: pointer;
`;

const SearchInput = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  font-size: 14px;
  min-width: 220px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const PageInfo = styled.span`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const DoseRulesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = searchParams.get("status") ?? "pending_review";
  const representativeOnly = searchParams.get("rep") !== "0"; // 기본 대표만
  const hasGateIssue = searchParams.get("gate") === "1";
  const q = searchParams.get("q") ?? "";
  const offset = Math.max(0, parseInt(searchParams.get("offset") ?? "0", 10) || 0);

  const [searchInput, setSearchInput] = useState(q);

  const query: FindDoseRulesQuery = {
    status: status === "all" ? undefined : (status as DoseRuleStatus),
    representativeOnly: representativeOnly ? true : undefined,
    hasGateIssue: hasGateIssue ? true : undefined,
    q: q.trim() || undefined,
    limit: LIMIT,
    offset,
  };

  const { data, isLoading, isError, refetch, isFetching } = useDoseRules(query);
  const stats = useDoseRuleStats();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = Math.floor(offset / LIMIT) + 1;
  const hasNext = offset + items.length < total;
  const hasPrev = offset > 0;

  /** 필터 변경: 패치 적용 + offset 0으로 리셋. */
  const applyFilter = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null) next.delete(key);
      else next.set(key, value);
    });
    next.set("offset", "0");
    setSearchParams(next);
  };

  const goToOffset = (nextOffset: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("offset", String(Math.max(0, nextOffset)));
    setSearchParams(next);
  };

  const submitSearch = () => {
    applyFilter({ q: searchInput.trim() || null });
  };

  const handleRowClick = (item: AdminDoseRuleListItem) => {
    navigate(`/admin/dose-rules/${encodeURIComponent(item.ruleId)}`, {
      state: {
        itemCode: item.itemCode,
        // 이전/다음 이동용 현재 페이지 순서(계획 D14)
        listItems: items.map((it) => ({ ruleId: it.ruleId, itemCode: it.itemCode })),
        listIndex: items.findIndex((it) => it.ruleId === item.ruleId),
        listSearch: searchParams.toString(),
      },
    });
  };

  const openTestPanel = () => {
    openDoseReviewTestSlide({
      onOpenRule: (ruleId: string) => {
        // 룰 상세로 이동 전 패널을 명시적으로 닫는다(전역 마운트라 라우트 변경으로 안 닫힘, 설계 결정 12).
        SlideManager.hide();
        navigate(`/admin/dose-rules/${encodeURIComponent(ruleId)}`);
      },
    });
  };

  return (
    <PageContainer>
      <HeaderRow>
        <Title>용량 룰 검수</Title>
        <Button
          buttonType="button"
          type="Text"
          size="Small"
          variant="Contained"
          color="Primary"
          onClick={openTestPanel}
          data-testid="dose-open-test-panel"
        >
          처방 판정 테스트
        </Button>
      </HeaderRow>
      <Subtitle>
        LLM이 추출한 용량 룰을 허가사항 원문과 대조해 승인/반려합니다. 중앙 룰(약국 P-룰 제외)만
        표시합니다.
      </Subtitle>

      <StatsRow>
        <StatCard>
          <StatLabel>검수 대기</StatLabel>
          <StatValue>{stats.data?.byStatus?.pendingReview ?? "—"}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>승인됨</StatLabel>
          <StatValue>{stats.data?.byStatus?.active ?? "—"}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>반려/보관</StatLabel>
          <StatValue>{stats.data?.byStatus?.archived ?? "—"}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>차단</StatLabel>
          <StatValue>{stats.data?.byStatus?.gateBlocked ?? "—"}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>대표 / 전개</StatLabel>
          <StatValue>
            {stats.data
              ? `${stats.data.representativeCount ?? "—"} / ${stats.data.derivedCount ?? "—"}`
              : "—"}
          </StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>검산이슈</StatLabel>
          <StatValue>{stats.data?.withGateIssueCount ?? "—"}</StatValue>
        </StatCard>
      </StatsRow>

      <Controls>
        <Select
          value={status}
          onChange={(e) => applyFilter({ status: e.target.value })}
          aria-label="상태 필터"
        >
          {FILTERABLE_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>

        <CheckLabel>
          <input
            type="checkbox"
            checked={representativeOnly}
            onChange={(e) => applyFilter({ rep: e.target.checked ? null : "0" })}
          />
          대표 룰만
        </CheckLabel>

        <CheckLabel>
          <input
            type="checkbox"
            checked={hasGateIssue}
            onChange={(e) => applyFilter({ gate: e.target.checked ? "1" : null })}
          />
          검산이슈만
        </CheckLabel>

        <SearchInput
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submitSearch();
          }}
          placeholder="품목명·품목코드·표준코드·rule_id"
          aria-label="검색"
        />
        <Button
          buttonType="button"
          type="Text"
          size="Small"
          variant="Contained"
          color="Primary"
          onClick={submitSearch}
        >
          검색
        </Button>
      </Controls>

      <DoseRuleListSection
        items={items}
        isLoading={isLoading}
        isError={isError}
        onRowClick={handleRowClick}
        onRetry={() => refetch()}
        emptyText={status === "gate_blocked" ? "차단된 룰이 없습니다" : undefined}
      />

      <Pagination>
        <PageInfo>
          {page}페이지 · 전체 {total}건{isFetching ? " · 갱신 중…" : ""}
        </PageInfo>
        <Button
          buttonType="button"
          type="Text"
          size="Small"
          variant="Outlined"
          color="Secondary"
          disabled={!hasPrev}
          disabledColor
          onClick={() => goToOffset(offset - LIMIT)}
        >
          이전
        </Button>
        <Button
          buttonType="button"
          type="Text"
          size="Small"
          variant="Outlined"
          color="Secondary"
          disabled={!hasNext}
          disabledColor
          onClick={() => goToOffset(offset + LIMIT)}
        >
          다음
        </Button>
      </Pagination>
    </PageContainer>
  );
};

export default DoseRulesPage;
